import { formatCurrency } from "@/lib/utils";
import { AffiliateStatsCard } from "./affiliate-stats-card";
import type { AffiliateStats } from "../types";
import {
  type DateRangeValue,
  isCustomRange,
} from "@/components/ui/date-range-selector";
import { format } from "date-fns";

type AffiliateOverviewCardProps = {
  stats: AffiliateStats | undefined;
  isLoading: boolean;
  dateRange: DateRangeValue;
};

// Helper function to get period label for subtitle
function getPeriodSubtitle(dateRange: DateRangeValue): string {
  if (isCustomRange(dateRange)) {
    return `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`;
  }
  
  const labels: Record<string, string> = {
    today: "today",
    yesterday: "yesterday",
    this_week: "this week",
    last_week: "last week",
    this_month: "this month",
    last_month: "last month",
    last_7_days: "last 7 days",
    last_30_days: "last 30 days",
    last_90_days: "last 90 days",
    ytd: "year to date",
    all: "all time",
  };
  return labels[dateRange] || "this period";
}

export const AffiliateOverviewCard = ({ stats, isLoading, dateRange }: AffiliateOverviewCardProps) => {
  const periodSubtitle = getPeriodSubtitle(dateRange);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="rounded-xl bg-card p-3 border border-border/50 animate-pulse h-[90px]"
          />
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="rounded-xl bg-card p-6 border border-border/50 text-center">
        <p className="text-muted-foreground">No stats available</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Row 1: Earnings, Referrals, Withdrawals, All-Time Earnings */}
      
      {/* Earnings (period-filtered) */}
      <AffiliateStatsCard
        title="Earnings"
        value={`$${formatCurrency(stats.periodEarnings || 0, 2)}`}
        subtitle={periodSubtitle}
        className="bg-gradient-to-br from-emerald-500/10 via-card to-card border-emerald-500/20"
        valueClassName="text-emerald-500"
      />

      {/* Referrals (period-filtered) */}
      <AffiliateStatsCard
        title="Referrals"
        value={stats.periodReferrals || 0}
        subtitle={periodSubtitle}
      />

      {/* Withdrawals (period-filtered) */}
      <AffiliateStatsCard
        title="Withdrawals"
        value={`$${formatCurrency(stats.periodWithdrawals || 0, 2)}`}
        subtitle={periodSubtitle}
      />

      {/* All-Time Earnings (reference, never filtered) */}
      <AffiliateStatsCard
        title="All-Time Earnings"
        value={`$${formatCurrency(stats.allTimeEarnings || 0, 2)}`}
        subtitle="total earned"
        className="bg-gradient-to-br from-amber-500/10 via-card to-card border-amber-500/20"
        valueClassName="text-amber-500"
      />

      {/* Row 2: Clicks, Conversion Rate, Volume, Performance Vault */}

      {/* Clicks (period-filtered) */}
      <AffiliateStatsCard
        title="Clicks"
        value={(stats.periodClicks || 0).toLocaleString()}
        subtitle={periodSubtitle}
        className="bg-gradient-to-br from-violet-500/10 via-card to-card border-violet-500/20"
        valueClassName="text-violet-500"
      />

      {/* Conversion Rate (period-filtered) */}
      <AffiliateStatsCard
        title="Conversion Rate"
        value={`${(stats.periodConversionRate || 0).toFixed(1)}%`}
        subtitle={periodSubtitle}
        className="bg-gradient-to-br from-cyan-500/10 via-card to-card border-cyan-500/20"
        valueClassName="text-cyan-500"
      />

      {/* Volume (period-filtered) */}
      <AffiliateStatsCard
        title="Volume"
        value={`$${formatCurrency(stats.periodVolume || 0, 0)}`}
        subtitle={periodSubtitle}
      />

      {/* Performance Vault (Locked) - Bottom Right */}
      <div className="rounded-xl bg-card p-3 border border-border/50 shadow-sm dark:shadow-none opacity-60">
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium truncate">
              Performance Vault
            </p>
            <p className="text-xs text-muted-foreground truncate shrink-0">
              Coming Soon
            </p>
          </div>
          <p className="text-lg md:text-xl font-bold tracking-tight truncate text-muted-foreground">
            —
          </p>
        </div>
      </div>
    </div>
  );
};
